import DownloadIcon from '@mui/icons-material/Download'
import { Box, Button, Card, CardContent, CircularProgress, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIsMobile } from '../hooks/useResponsive'
import dayjs from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import PriceCheckIcon from '@mui/icons-material/PriceCheck'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useAppDispatch, useAppSelector } from '../store'
import { apiSlice } from '../store/api/apiSlice'
import { API_BASE_URL } from '../config/api'
import { usePagePermission } from '../hooks/usePagePermission'
import { usePreference } from '../hooks/usePreference'
import PageHeader from '../components/PageHeader'
import ReloadButton from '../components/ReloadButton'
import {
  CARD_CONTENT_COMPACT,
  FILTER_HEADER_INPUT_SX,
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  SHOW_ON_TABLET_INLINE,
} from '../theme/responsive'
import FuturesAllocationListView from './futures/FuturesAllocationListView'
import FuturesBankCashInTab from './futures/bankCashIn/FuturesBankCashInTab'
import FuturesBankStatementView from './futures/FuturesBankStatementView'
import FuturesGenericListView from './futures/FuturesGenericListView'
import FuturesLocalPriceDialog from './futures/FuturesLocalPriceDialog'
import FuturesMarginReportPage from './futures/FuturesMarginReportPage'
import FuturesMarketView from './futures/FuturesMarketView'
import FuturesOrdersListView from './futures/FuturesOrdersListView'
import FuturesReportView from './futures/FuturesReportView'
import FuturesSummaryReportPage from './futures/FuturesSummaryReportPage'
import FuturesTabSummary from './futures/FuturesTabSummary'
import SpreadsOrdersListView from './futures/SpreadsOrdersListView'
import {
  FUTURES_TAB_CONFIG,
  FUTURES_TAB_NAV_MAIN,
  isFuturesTabKey,
  type FuturesTabKey,
} from './futures/futuresControlTabConfig'
import {
  futuresAccessCaptionSx,
  futuresAccessTabPanelSx,
} from './futures/futuresAccessUi'
import { useSnackbar } from '../hooks/useSnackbar'

const headerButtonSx = {
  minWidth: 0, // cho phép button icon-only compact (MUI default 64px → buộc pack 1 row mobile)
  minHeight: 24,
  px: { xs: 0.5, sm: 0.75 },
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
  '& .MuiButton-startIcon': {
    // xs icon-only: bỏ marginRight startIcon vì không có text bên cạnh
    mr: { xs: 0, sm: 0.5 },
    ml: 0,
  },
}

const getFiscalYearDefaultRange = () => {
  const now = dayjs()
  const startYear = now.month() >= 9 ? now.year() : now.year() - 1

  return {
    startDate: `${startYear}-10-01`,
    endDate: `${startYear + 1}-09-30`,
  }
}

/** Giống Access `btnExportExcel_Click` — `form_frmFuturesMain.txt`. */
const FUTURES_EXPORT_TOKEN = 'JDHK-NDEB-DBCK-DGEJ'

const FUTURES_TAB_PATH_BY_KEY: Partial<Record<FuturesTabKey, string>> = {
  pucontract: 'pucontract',
  'local-s': 'sacontract',
}

const FUTURES_TAB_KEY_BY_PATH: Partial<Record<string, FuturesTabKey>> = {
  pucontract: 'pucontract',
  sacontract: 'local-s',
}

const getFuturesTabPath = (key: FuturesTabKey) => FUTURES_TAB_PATH_BY_KEY[key] ?? key

const getFuturesTabKeyFromPath = (segment: string | undefined): FuturesTabKey | null => {
  if (!segment) return null
  const mapped = FUTURES_TAB_KEY_BY_PATH[segment]
  if (mapped) return mapped
  return isFuturesTabKey(segment) ? segment : null
}

/**
 * Map tab → chế độ filter date ở header (label + cột BE filter).
 *  - `disabled`: header date pickers ẩn-không-tương-tác, không truyền xuống tab
 *  - `date`: tab filter theo cột `date` (Orders / Spreads / Allocation)
 *  - `delivery-latest`: Purchase — cột `latest_delivery`
 *  - `shipment-period`: SA Contract — cột `delivery_from`
 */
type DateFilterMode = 'disabled' | 'date' | 'delivery-latest' | 'shipment-period'

function getDateFilterMode(key: FuturesTabKey): DateFilterMode {
  switch (key) {
    case 'allocation':
    case 'orders':
    case 'spreads':
    case 'bank-cash-in':
    case 'bank-statement':
      return 'date'
    case 'pucontract':
      return 'delivery-latest'
    case 'local-s':
      return 'shipment-period'
    default:
      return 'disabled'
  }
}

const DATE_FILTER_LABEL: Record<DateFilterMode, string> = {
  disabled: 'Date Period',
  date: 'Date',
  'delivery-latest': 'Delivery Latest',
  'shipment-period': 'Shipment Period',
}

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

function renderTab(key: FuturesTabKey, embedded: boolean) {
  switch (key) {
    case 'orders':
      return <FuturesOrdersListView embedded={embedded} />
    case 'spreads':
      return <SpreadsOrdersListView embedded={embedded} />
    case 'allocation':
      return <FuturesAllocationListView embedded={embedded} />
    case 'adjust-lot':
      return <FuturesGenericListView kind="adjust-lot" embedded={embedded} />
    case 'bank-cash-in':
      return <FuturesBankCashInTab />
    case 'bank-statement':
      return <FuturesBankStatementView embedded={embedded} />
    case 'bank-cost-change':
      return <FuturesGenericListView kind="bank-cost-change" embedded={embedded} />
    case 'market':
      return <FuturesMarketView embedded={embedded} />
    case 'pucontract':
      return <FuturesGenericListView kind="pucontract" embedded={embedded} />
    case 'local-s':
      return <FuturesGenericListView kind="local-s" embedded={embedded} />
    case 'margin-report':
      return <FuturesReportView kind="margin-report" embedded={embedded} />
    case 'summary':
      return <FuturesReportView kind="summary" embedded={embedded} />
    case 'cash-balance':
      return <FuturesReportView kind="cash-balance" embedded={embedded} />
    case 'position':
      return <FuturesReportView kind="position" embedded={embedded} />
    case 'pnl':
      return <FuturesReportView kind="pnl" embedded={embedded} />
    case 'check-futures':
      return <FuturesReportView kind="check-futures" embedded={embedded} />
    case 'reconciliation':
      return <FuturesReportView kind="reconciliation" embedded={embedded} />
    default:
      return <FuturesReportView kind="summary" embedded={embedded} />
  }
}

export default function FuturesControlPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const [localPriceOpen, setLocalPriceOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const { canEdit } = usePagePermission()
  const email = useAppSelector((s) => s.auth.user?.email ?? '')
  const dispatch = useAppDispatch()

  // Date filter chung cho mọi tab Futures Control — generic preference registry.
  //   scope='futures', key='page_filter' → DB row `(futures, list_filter)`
  // BE legacy list-query services tiếp tục đọc cùng row → switch transparent.
  // Default = fiscal year (Oct → Sep) khi baseline chưa cover.
  interface FuturesPageFilter extends Record<string, unknown> {
    dateFrom: string | null
    dateTo: string | null
  }
  const filter = usePreference<FuturesPageFilter>('futures', 'page_filter')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [filtersReady, setFiltersReady] = useState(false)
  const prefsHydrated = useRef(false)
  const lastPatchedRef = useRef<{ dateFrom: string; dateTo: string } | null>(null)

  useEffect(() => {
    if (filter.isLoading || prefsHydrated.current) return
    prefsHydrated.current = true

    const fiscalYearDefault = getFiscalYearDefaultRange()
    const savedFrom = filter.value.dateFrom ?? ''
    const savedTo = filter.value.dateTo ?? ''
    const hydratedFrom = savedFrom || fiscalYearDefault.startDate
    const hydratedTo = savedTo || fiscalYearDefault.endDate

    lastPatchedRef.current = { dateFrom: hydratedFrom, dateTo: hydratedTo }
    setDateFrom(hydratedFrom)
    setDateTo(hydratedTo)

    const defaultsApplied = !savedFrom || !savedTo
    const finishSetup = async () => {
      if (defaultsApplied) {
        try {
          await filter.patch({ dateFrom: hydratedFrom, dateTo: hydratedTo })
        } catch {
          // ignore — vẫn render (BE đã có default fallback)
        }
      }
      setFiltersReady(true)
    }
    void finishSetup()
  }, [filter])

  // Debounced PATCH khi user đổi date (mirror SaleContract 450ms).
  // Sau khi PATCH xong → dispatch invalidate `FuturesControl` để các tab refetch
  // với filter mới (mirror hành vi nút Reload). BE đọc dateFrom/dateTo từ
  // user_preferences ⇒ phải chờ patch resolve trước khi refetch để query đọc filter
  // mới, không còn dùng filter stale.
  useEffect(() => {
    if (!prefsHydrated.current) return
    const last = lastPatchedRef.current
    if (last && last.dateFrom === dateFrom && last.dateTo === dateTo) return
    const timer = window.setTimeout(async () => {
      lastPatchedRef.current = { dateFrom, dateTo }
      try {
        await filter.patch({ dateFrom, dateTo })
      } catch {
        return // patch lỗi → giữ data cũ, không trigger reload (BE đã có fallback)
      }
      dispatch(apiSlice.util.invalidateTags(['FuturesControl']))
    }, 450)
    return () => window.clearTimeout(timer)
  }, [dateFrom, dateTo, filter, dispatch])

  const hasInvalidDateRange =
    Boolean(dateFrom) && Boolean(dateTo) && dateFrom > dateTo

  const handleExportExcel = async () => {
    if (!email) return
    setExporting(true)
    try {
      // Current app exposes this export under the legacy mount.
      const url = `${API_BASE_URL}/erp-legacy/download/futures/data?token=${encodeURIComponent(FUTURES_EXPORT_TOKEN)}&email=${encodeURIComponent(email)}`
      const res = await fetch(url)
      if (!res.ok) throw new Error('Excel export failed.')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      a.href = href
      a.download = filename ?? 'Futures.xlsx'
      a.click()
      URL.revokeObjectURL(href)
      snack.success('Excel file downloaded.')
    } catch (e: unknown) {
      snack.error(e instanceof Error ? e.message : 'Excel export failed.')
    } finally {
      setExporting(false)
    }
  }

  const activeKey: FuturesTabKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    return getFuturesTabKeyFromPath(segments[1]) ?? 'summary'
  }, [location.pathname])

  const activeTab = FUTURES_TAB_CONFIG.find((tab) => tab.key === activeKey) ?? FUTURES_TAB_CONFIG[0]
  const mainTabValue = FUTURES_TAB_NAV_MAIN.some((t) => t.key === activeKey) ? activeKey : 'summary'

  // Filter date theo tab: label dynamic + disable trên các tab không apply.
  // BE đọc dateFrom/dateTo từ user_preferences ⇒ FE chỉ PATCH khi user đổi,
  // PATCH invalidate `FuturesControl` tag → các tab tự refetch với filter mới.
  const dateFilterMode = getDateFilterMode(activeKey)
  const dateFilterLabel = DATE_FILTER_LABEL[dateFilterMode]
  const dateFilterDisabled = dateFilterMode === 'disabled'

  // Mỗi lần đổi tab → invalidate cache + 1 frame loading transition để API
  // tab đích luôn refetch fresh, đồng thời hiển thị Loading state liền mạch.
  const isInitialMountRef = useRef(true)
  const [mountedKey, setMountedKey] = useState<FuturesTabKey | null>(activeKey)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      setMountedKey(activeKey)
      return
    }
    dispatch(apiSlice.util.invalidateTags(['FuturesControl']))
    setMountedKey(null)
    const raf = requestAnimationFrame(() => setMountedKey(activeKey))
    return () => cancelAnimationFrame(raf)
  }, [activeKey, dispatch])

  // Endpoint names theo tab — nút Reload "busy" khi 1 trong các query này đang
  // pending. Tên endpoint xem futuresControlApi.ts.
  const watchedEndpoints = useMemo<string[]>(() => {
    switch (activeKey) {
      case 'orders':
        return ['getFuturesOrders']
      case 'spreads':
        return ['getFuturesSpreads']
      case 'allocation':
        return ['getFuturesAllocations', 'getFuturesAllocationsNoalc']
      case 'adjust-lot':
        return ['getAdjustLotRows']
      case 'bank-cash-in':
        return ['getFuturesBankCash']
      case 'bank-statement':
        return ['getFuturesBankStatement']
      case 'bank-cost-change':
        return ['getBankCostChangeRows']
      case 'market':
        return ['getMarketPrices']
      case 'pucontract':
        return ['getLocalPFutures']
      case 'local-s':
        return ['getLocalS']
      case 'summary':
      case 'margin-report':
      case 'cash-balance':
      case 'position':
      case 'pnl':
      case 'check-futures':
      case 'reconciliation':
        return ['getFuturesReport']
      default:
        return []
    }
  }, [activeKey])

  useEffect(() => {
    if (!location.pathname.startsWith('/futures')) return

    const segments = location.pathname.split('/').filter(Boolean)
    if (!getFuturesTabKeyFromPath(segments[1])) {
      navigate('/futures/summary', { replace: true })
    }
  }, [location.pathname, navigate])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Futures Control"
        module="Futures"
        actions={
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            sx={{ width: '100%' }}
          >
            {/* Pattern 11 date range convention 2026-05-28:
                  - DatePicker đầu: label = dateFilterLabel (dynamic per tab mode)
                  - Icon `→` giữa phân biệt from → to
                  - DatePicker thứ 2: KHÔNG label
                  - aria-label phân biệt start/end cho screen reader.
                Width override xs 108 (FILTER_HEADER_INPUT_SX default 130) để
                cả filter row pack 1 hàng trên iPhone SE 375px (yêu cầu chị Đẹp). */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={dateFilterLabel}
                format="DD-MMM-YYYY"
                disabled={dateFilterDisabled}
                value={dateFrom ? dayjs(dateFrom) : null}
                onChange={(d) =>
                  setDateFrom(d ? d.format('YYYY-MM-DD') : getFiscalYearDefaultRange().startDate)
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { ...FILTER_HEADER_INPUT_SX, width: { xs: 108, sm: 158 } },
                    InputLabelProps: { shrink: true },
                    inputProps: { 'aria-label': `${dateFilterLabel} start` },
                  },
                }}
              />
              <ArrowRightAltIcon
                fontSize="small"
                sx={{
                  color: dateFilterDisabled ? 'text.disabled' : 'text.secondary',
                  fontSize: { xs: 14, sm: 18 },
                  mx: { xs: 0, sm: 0.25 },
                }}
                aria-hidden="true"
              />
              <DatePicker
                format="DD-MMM-YYYY"
                disabled={dateFilterDisabled}
                value={dateTo ? dayjs(dateTo) : null}
                onChange={(d) =>
                  setDateTo(d ? d.format('YYYY-MM-DD') : getFiscalYearDefaultRange().endDate)
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    error: !dateFilterDisabled && hasInvalidDateRange,
                    sx: { ...FILTER_HEADER_INPUT_SX, width: { xs: 108, sm: 158 } },
                    inputProps: { 'aria-label': `${dateFilterLabel} end` },
                  },
                }}
              />
            </LocalizationProvider>
            {/* Pattern 8 progressive 3-level: xs icon-only (PriceCheck), sm "Price"
                (short), md+ "Input Local Price" (full). Tiết kiệm chiều ngang để
                filter row pack 1 hàng iPhone SE. */}
            {canEdit ? (
              <Button
                size="small"
                variant="outlined"
                sx={headerButtonSx}
                onClick={() => setLocalPriceOpen(true)}
                title="Input Value"
                startIcon={<PriceCheckIcon sx={{ fontSize: 14 }} />}
              >
                <Box component="span" sx={SHOW_ON_TABLET_INLINE}>Value</Box>
                <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Input Value</Box>
              </Button>
            ) : (
              <Tooltip title="Edit permission required for the Futures screen.">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={headerButtonSx}
                    disabled
                    startIcon={<PriceCheckIcon sx={{ fontSize: 14 }} />}
                  >
                    <Box component="span" sx={SHOW_ON_TABLET_INLINE}>Value</Box>
                    <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Input Value</Box>
                  </Button>
                </span>
              </Tooltip>
            )}
            {/* xs: icon-only (filter row pack chật, RefreshIcon + tooltip đủ
                semantic). sm+: giữ label "Reload" để consistent với các page khác. */}
            <ReloadButton
              sx={headerButtonSx}
              label={isMobile ? '' : 'Reload'}
              apiTags={['FuturesControl']}
              baseTags={[]}
              watchEndpoints={watchedEndpoints}
            />
            {/* Pattern 2: xs icon-only (DownloadIcon), sm+ full text. */}
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportExcel}
              disabled={exporting || !email}
              title="Export (All)"
              startIcon={
                exporting ? <CircularProgress size={11} color="inherit" /> : <DownloadIcon fontSize="small" />
              }
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Export (All)</Box>
            </Button>
          </Stack>
        }
      />
      {hasInvalidDateRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={mainTabValue}
          onChange={(_, value: FuturesTabKey) => {
            navigate(`/futures/${getFuturesTabPath(value)}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 34,
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: 34,
              minWidth: 0,
              py: 0.25,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {FUTURES_TAB_NAV_MAIN.map((tab) => {
            // Purchase + SA Contract: highlight bằng brand green để dễ nhận diện.
            const isContractTab = tab.key === 'pucontract' || tab.key === 'local-s'
            return (
              <Tab
                key={tab.key}
                label={tab.label}
                value={tab.key}
                sx={
                  isContractTab
                    ? {
                        color: 'success.main',
                        fontWeight: 700,
                        '&.Mui-selected': { color: 'success.dark' },
                      }
                    : undefined
                }
              />
            )
          })}
        </Tabs>
        <CardContent sx={CARD_CONTENT_COMPACT}>
          {!filtersReady || mountedKey === null ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
              <CircularProgress size={28} />
            </Box>
          ) : mountedKey === 'summary' ? (
            <FuturesSummaryReportPage />
          ) : mountedKey === 'margin-report' ? (
            <FuturesMarginReportPage />
          ) : mountedKey === 'market' ||
            mountedKey === 'allocation' ||
            mountedKey === 'orders' ||
            mountedKey === 'spreads' ||
            mountedKey === 'pucontract' ||
            mountedKey === 'local-s' ||
            mountedKey === 'bank-cash-in' ||
            mountedKey === 'bank-statement' ||
            mountedKey === 'adjust-lot' ||
            mountedKey === 'bank-cost-change' ? (
            // Giống /sales/contract/list — render thẳng trong Card (Orders/Spreads/Market/…), không panel caption + FuturesTabSummary.
            renderTab(mountedKey, false)
          ) : (
            <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
              <Typography sx={{ ...futuresAccessCaptionSx, fontSize: '0.95rem' }}>
                {activeTab.label}
              </Typography>
              <FuturesTabSummary tab={activeTab} />
              <Box sx={{ ...futuresAccessTabPanelSx, p: 1, overflow: 'auto' }}>
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  {renderTab(mountedKey, true)}
                </Box>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      <FuturesLocalPriceDialog open={localPriceOpen} onClose={() => setLocalPriceOpen(false)} canSave={!!canEdit} />
    </Box>
  )
}
